import type { Menu } from "types/menu"
import type { Customer } from "types/customer"
import { universalMenuItems } from "./universal"
import { getCustomerConfig } from "config/customerConfigurations"
import { isWorkPackagesHidden } from "utils/workPackagesHidden"
import { isAutomaticDetectionsHidden } from "utils/automaticDetectionsHidden"

type RoadsMenuOptions = {
    customerData?: Customer
    showInternalItems: boolean
}

export const getRoadsMenu = ({ customerData, showInternalItems = false }: RoadsMenuOptions): Menu => {
    const assetManagementSystemName = customerData?.customerID
        ? getCustomerConfig(customerData.customerID).assetManagement.systemName
        : null

    const baseItems = [
        ...[
            {
                label: "Dashboard",
                icon: "pi pi-fw pi-chart-line",
                to: "/dashboard",
            },
            // ART-3118: the whole Assignments surface (packages + legacy
            // assignments) disappears for hide-work-packages customers —
            // deep links to existing items keep working.
            ...(isWorkPackagesHidden(customerData)
                ? []
                : [
                      {
                          label: "Repairs",
                          // The box is the work-package mark (tray, toggles, package header) —
                          // the nav item wears PrimeIcons' cube so the destination matches.
                          icon: "pi pi-fw pi-box",
                          to: "/work-items",
                      },
                  ]),
            {
                id: "detections-v2",
                label: "Detections V2",
                icon: "pi pi-fw pi-eye",
                to: "/detections-v2",
            },
            ...(assetManagementSystemName
                ? [
                      {
                          label: `My ${assetManagementSystemName} Work`,
                          icon: "pi pi-fw pi-list",
                          to: "/confirm-exports",
                      },
                  ]
                : []),
            {
                label: "Route View",
                icon: "",
                to: "/route-view",
            },
            {
                // ART-3002: exports are customer-facing now — Detections V2's
                // "All matching filters" export lands here (progress + downloads).
                label: "Exports",
                icon: "pi pi-fw pi-download",
                to: "/exports",
            },
            {
                // Promoted out of the Beta section (Aug 2026) — kept at the
                // bottom of Overview, with Route View still above it, so both
                // viewers stay reachable while V2 earns the default spot.
                label: "Route View V2",
                icon: "pi pi-fw pi-map",
                to: "/route-view-v2",
            },
        ],
        ...(showInternalItems ? [] : []),
    ]

    const roadConditionItems = [
        {
            // Promoted out of the Internal section (Sep 2026): the route is no
            // longer super-admin only, so customers browse their network here.
            // Customers know it as "Road Condition V2" (in code it's Network
            // View). V2 until the legacy Road Condition page is retired, like
            // Detections V2 / Route View V2 — then all three drop the suffix.
            label: "Road Condition V2",
            icon: "pi pi-globe",
            to: "/network-view",
        },
        {
            label: "Defects Guide",
            icon: "pi pi-chart-bar",
            to: "/road-condition-defects-guide",
        },
    ]

    const baseMenu = [
        {
            label: "Overview",
            items: baseItems,
        },
        { separator: true },
        {
            label: "Road Condition",
            items: roadConditionItems,
        },
        { separator: true },
        {
            label: "Legacy",
            items: [
                {
                    id: "detections-legacy",
                    label: "Detections",
                    icon: "dashboard",
                    to: "/detections",
                    badge: "LEGACY",
                },
                ...(!customerData?.confirmDetails
                    ? [
                          {
                              label: "Repairs",
                              icon: "CheckSquare",
                              to: "/repairs",
                              badge: "LEGACY",
                          },
                      ]
                    : []),
                // Every sign is an automatic detection, so the page would be
                // empty for hide-automatic-detections customers.
                ...(isAutomaticDetectionsHidden(customerData)
                    ? []
                    : [
                          {
                              label: "Signs",
                              icon: "pi pi-fw pi-chart-line",
                              to: "/signs",
                              badge: "LEGACY",
                          },
                      ]),
                {
                    // superseded by Network View (Sep 2026)
                    label: "Road Condition",
                    icon: "pi pi-chart-bar",
                    to: "/road-condition",
                    badge: "LEGACY",
                },
            ],
        },
        { separator: true },
    ]

    if (showInternalItems) {
        return [
            ...baseMenu,
            {
                label: "Internal",
                items: [
                    {
                        label: "Customer Configuration",
                        icon: "pi pi-fw pi-building",
                        to: "/customers",
                    },
                    {
                        label: "Analytics",
                        icon: "pi pi-fw pi-chart-line",
                        to: "/analytics",
                    },
                    {
                        label: "Device Management",
                        icon: "pi pi-fw pi-tablet",
                        to: "/device-management",
                    },
                    {
                        label: "Devices",
                        icon: "",
                        to: "/devices",
                    },
                    {
                        label: "Integration Status",
                        icon: "pi pi-cloud-upload",
                        to: "/integrations",
                    },
                    {
                        label: "Labelling",
                        icon: "pi pi-fw pi-tag",
                        to: "/labelling",
                    },
                ],
            },
            { separator: true },
            ...universalMenuItems(),
        ]
    }

    return [...baseMenu, ...universalMenuItems()]
}
